import { endOfDay, isAfter, isSameDay } from "date-fns";
import { z } from "zod";

import { type Payment, PaymentEventStatus } from "@/apis/gql/types";
import i18next from "i18next";
import { lineItemsValidationSchema } from "./line-item";
import { paymentSchema } from "./payment";

export const invoiceValidationSchema = z
  .object({
    invoiceId: z.string().min(1).max(50),
    invoiceNo: z.string().regex(/^[\w$%\-\/]{0,36}$/, {
      message:
        "Die Belegnummer muss 0 bis 36 Zeichen haben und darf nur Buchstaben, Ziffern und die Zeichen $, %, - und / beinhalten.",
    }),
    totalAmount: z
      .number()
      .refine((n) => n !== 0, { message: "Betrag darf nicht null sein" }),
    currency: z.string().min(1).max(3),
    invoiceDate: z.date(),
    split: z.boolean().nullable().optional(),
    isCorrectAmount: z.boolean().nullable().optional(),
    serviceDate: z.date().nullable(),
    supplierId: z.string().min(1).max(50),
    lineItems: lineItemsValidationSchema,
  })
  .refine(
    (data) => {
      if (
        data.invoiceDate &&
        data.serviceDate &&
        !isSameDay(data.invoiceDate, data.serviceDate) &&
        isAfter(data.serviceDate, data.invoiceDate)
      ) {
        return false;
      }

      return true;
    },
    {
      message: "Leistungsdatum darf nicht nach Rechnungsdatum liegen",
      path: ["invoiceDate"],
    },
  )
  .refine(
    (data) => {
      if (data.currency.toUpperCase() !== "EUR") {
        const today = new Date();

        if (data.invoiceDate && isAfter(data.invoiceDate, endOfDay(today))) {
          return false;
        }
      }

      return true;
    },
    {
      message:
        "Bei Femdwährungen darf das Rechnungsdatum nicht in der Zukunft liegen.",
      path: ["invoiceDate"],
    },
  )
  .refine(
    (data) => {
      if (data.currency.toUpperCase() !== "EUR" && data.serviceDate) {
        const today = new Date();

        if (isAfter(data.serviceDate, endOfDay(today))) {
          return false;
        }
      }

      return true;
    },
    {
      message:
        "Leistungsdatum darf nicht in der Zukunft liegen, wenn die Währung nicht Euro ist",
      path: ["serviceDate"],
    },
  )
  .refine(
    (data) => {
      if (data.lineItems.length === 0) return true;
      if (data.isCorrectAmount) return true;

      return false;
    },
    {
      message: "Summe der Positionen muss dem Gesamtbetrag entsprechen",
      path: ["lineItems"],
    },
  );

export const invoiceValidationClientFormSchema = z
  .object({
    invoiceId: z.string(),
    invoiceNo: z.string().optional().nullable(),
    supplierId: z.string(),
    totalAmount: z.number(),
    currency: z.string(),
    isPaid: z.boolean(),
    payments: z.any(),
  })
  .superRefine((data, ctx) => {
    if (data.isPaid) return;

    const draftPayments = (data.payments as Payment[]).filter(
      (p) => p.status === PaymentEventStatus.DRAFT,
    );

    const result = z.array(paymentSchema).safeParse(draftPayments);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue(issue);
      }
    }
  });
